import type { FC } from 'react';
import { memo, useContext, useEffect, useCallback } from 'react';

import { usePageSpaceKey } from '@confluence/page-context';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import type { RoutesContextType } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam.ts';

import { buildCreationLink } from './CreateContentRouteComponent/index.ts';

/** Query Param creation is Deprecated
 * This file joins and simplifies some query param listeners we had for creation.
 * Instead of doing the creation itself, it now forwards to /create-content route
 * The code structure is no longer strongly typed or easily extensible. This is to simplify/minimize code in an area that shouldnt be updated
 */
// combining query params from 2 sources
const queryParamKeys = {
	createContent: 'createContent',
	createBlankFabricPage: 'createBlankFabricPage',

	parentPageId: ['createContentParentPageId', 'parentPageId'],
	spaceKey: ['createContentSpaceKey', 'spaceKey'],
};

const parseQueryParams = (queryParams: ReturnType<RoutesContextType['getQueryParams']>) => {
	const createBlankFabricPage =
		getSingleParam(queryParams, queryParamKeys.createBlankFabricPage) === 'true';
	const contentType = getSingleParam(queryParams, queryParamKeys.createContent);
	const parentPageId =
		getSingleParam(queryParams, queryParamKeys.parentPageId[0]) ||
		getSingleParam(queryParams, queryParamKeys.parentPageId[1]);
	const spaceKey =
		getSingleParam(queryParams, queryParamKeys.spaceKey[0]) ||
		getSingleParam(queryParams, queryParamKeys.spaceKey[1]);

	return {
		createBlankFabricPage,
		contentType,
		parentPageId,
		spaceKey,
	};
};

export const CreateContentLegacyQueryParamRedirect: FC = memo(() => {
	const { getQueryParams, push } = useContext(RoutesContext);
	const [currentSpaceKey] = usePageSpaceKey();

	const queryParams = getQueryParams();
	const { contentType, spaceKey, parentPageId, createBlankFabricPage } =
		parseQueryParams(queryParams);

	const getCreationLinkOptions = useCallback(() => {
		if (createBlankFabricPage) {
			return {
				withFallback: true as const,
				contentType: 'page' as const,
				spaceKey: spaceKey || currentSpaceKey,
				parentPageId,
				source: 'legacyQueryParamRedirect-createBlankFabricPage',
			};
		}
		if (contentType) {
			return {
				contentType: contentType as any, // redirect target can decide for itself if it can handle a certain contentType
				spaceKey,
				parentPageId,
				source: 'legacyQueryParamRedirect-createContent',
			};
		}

		return null;
	}, [contentType, spaceKey, parentPageId, createBlankFabricPage, currentSpaceKey]);

	useEffect(() => {
		const creationLinkOptions = getCreationLinkOptions();
		if (creationLinkOptions) {
			push(buildCreationLink(creationLinkOptions));
		}
	}, [getCreationLinkOptions, push]);

	return null;
});
